<script lang="ts">
import { orderService } from "@/app/services/OrderService";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";
import { ref, defineComponent, computed } from "vue";
import SearchTargetingOrders from "@/views/orders/SearchTargetingOrders/SearchTargetingOrders.vue";
import OrderTab from "@/components/organisms/orders/OrderTab.vue";
import { orderStore } from "@/store/order";
import { authStore } from "@/store/auth";
import Ad from "@/components/organisms/common/Ad.vue";

const LIMIT = 50;

export default defineComponent({
  components: {
    SearchTargetingOrders,
    OrderTab,
    Ad
  },
  setup() {
    const loader = useLoader();
    const { showErrorModal } = useErrorModal();

    const showPages = ref(1);
    const isAllOrdersLoaded = ref(false);
    const existsLimitedOrders = ref(false);

    const fetchData = async () => {
      loader.show();
      try {
        await Promise.all([
          fetchCountOfLimitedOrders(),
          fetchIndividualOfferOrders(true)
        ]);
      } catch (e) {
        await showErrorModal(e as Error);
      } finally {
        loader.hide();
      }
    };

    const fetchIndividualOfferOrders = async (requireReload = false) => {
      if (!authStore.hasRegisteredPersonalInfo) {
        return;
      }
      if (requireReload) {
        // `OrdersDetail`以外のページから遷移してきた場合はクリアされる
        // ページ遷移でない場合(画面下部での追加読込など)ではクリアしない
        orderStore.clearIndividualOfferOrderList();
        orderStore.clearEvaluations();
      }
      loader.show();
      try {
        const orders = await orderService.fetchIndividualOfferOrders(
          LIMIT,
          (showPages.value - 1) * LIMIT
        );
        if (
          orders.length === 0 &&
          // 既に orderStore が存在しているワーカーの場合、ストアが初期化されず individualOfferOrderList = undefined の場合がある
          // そのため undefined の場合には `|| []` で空リストを対象に .length へアクセスする
          (orderStore.individualOfferOrderList || []).length > 0
        ) {
          isAllOrdersLoaded.value = true;
          return;
        }
        orderStore.addIndividualOfferOrderList([...orders]);
        showPages.value += 1;
      } catch (e) {
        await showErrorModal(e as Error);
      } finally {
        loader.hide();
      }
    };

    const fetchCountOfLimitedOrders = async () => {
      if (!authStore.hasRegisteredPersonalInfo) {
        return;
      }

      loader.show();
      try {
        const { count } = await orderService.fetchCountOfLimitedOrders();
        existsLimitedOrders.value = count > 0;
      } catch (e) {
        await showErrorModal(e as Error);
      } finally {
        loader.hide();
      }
    };

    // 再計算することで、ストアは更新されたが画面が更新されない状態を防ぐ
    const individualOfferOrders = computed(
      () => orderStore.individualOfferOrderList || []
    );

    return {
      individualOfferOrders,
      existsLimitedOrders,
      isAllOrdersLoaded,
      fetchData,
      fetchCountOfLimitedOrders,
      fetchIndividualOfferOrders
    };
  },
  beforeRouteEnter(to, from, next) {
    next(async vm => {
      const instance = vm;
      // `OrdersDetail`から遷移してきた場合は元のスクロール位置に戻したい
      // そのため案件一覧を再取得せず遷移前のまま表示させる。件数のみ再計算
      if (from.name === "OrdersDetail") {
        // @ts-ignore
        await instance.fetchCountOfLimitedOrders();
        return;
      }
      // @ts-ignore
      await instance.fetchData(true);
    });
  }
});
</script>
<template>
  <SearchTargetingOrders
    :orders="individualOfferOrders"
    :isAllOrdersLoaded="isAllOrdersLoaded"
    transitionSource="SearchIndividualOfferOrders"
    pageTitleWhenEmpty="指名オファーがありません"
    @fetch-orders="fetchIndividualOfferOrders"
  >
    <template v-slot:orderTab>
      <OrderTab
        :orderType="'individual'"
        :existsLimitedOrder="existsLimitedOrders"
        :existsIndividualOrder="individualOfferOrders.length > 0"
      />
    </template>

    <template v-slot:ad>
      <!-- AD: search_individual_offer_order -->
      <Ad adSlot="search_individual_offer_order" class="ad-slot-search" />
    </template>

    <template v-slot:emptyDescription>
      勤務した事業所から評価されると <br />
      あなたに特別なオファーが送られます <br />
      実績を重ねて指名オファーを獲得しましょう！
    </template>
  </SearchTargetingOrders>
</template>

<style lang="scss" scoped>
.ad-slot-search {
  margin: 6px 8px 0;
}
</style>
